import { supabase } from '../lib/supabase';

export const embedService = {
  // Get embed configuration for a catalog
  async getEmbedConfig(catalogId) {
    try {
      const { data, error } = await supabase?.from('embed_configurations')?.select('*')?.eq('catalog_id', catalogId)?.single();

      // If no config exists (PGRST116), return null with default config
      if (error) {
        if (error?.code === 'PGRST116') {
          // No configuration found - return default config
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching embed config:', error);
      throw error;
    }
  },

  // Save or update embed configuration
  async saveEmbedConfig(catalogId, companyId, config) {
    try {
      const embedConfig = {
        catalog_id: catalogId,
        company_id: companyId,
        width: config?.width || '100%',
        height: config?.height || '800',
        responsive: config?.responsive !== undefined ? config?.responsive : true,
        initial_page: config?.initialPage || 1,
        theme: config?.theme || 'light',
        show_navigation: config?.showNavigation !== undefined ? config?.showNavigation : true,
        show_sharing: config?.showSharing !== undefined ? config?.showSharing : true,
        show_zoom: config?.showZoom !== undefined ? config?.showZoom : true,
        show_fullscreen: config?.showFullscreen !== undefined ? config?.showFullscreen : true,
        show_download: config?.showDownload !== undefined ? config?.showDownload : false,
        show_menu: config?.showMenu !== undefined ? config?.showMenu : false,
        show_search: config?.showSearch !== undefined ? config?.showSearch : false,
        default_language: config?.defaultLanguage || 'es',
        viewer_mode: config?.viewerMode || 'flip',
        allowed_domains: config?.allowedDomains || null,
        require_referrer: config?.requireReferrer || false,
        custom_css: config?.customCSS || null,
        primary_color: config?.primaryColor || null,
        enable_analytics: config?.enableAnalytics !== undefined ? config?.enableAnalytics : true,
        ga4_id: config?.ga4Id || null,
        track_hotspots: config?.trackHotspots !== undefined ? config?.trackHotspots : true,
        lazy_load: config?.lazyLoad !== undefined ? config?.lazyLoad : true,
        preload_adjacent: config?.preloadAdjacent !== undefined ? config?.preloadAdjacent : true
      };

      const { data, error } = await supabase?.from('embed_configurations')?.upsert(embedConfig, { onConflict: 'catalog_id' })?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error saving embed config:', error);
      throw error;
    }
  },

  // Generate iframe embed code with configuration
  generateIframeEmbed(catalogId, config = {}) {
    const baseUrl = window?.location?.origin;
    const params = new URLSearchParams({
      page: config.initialPage || 1,
      theme: config.theme || 'light',
      nav: config.showNavigation ? '1' : '0',
      share: config.showSharing ? '1' : '0',
      zoom: config.showZoom ? '1' : '0',
      fullscreen: config.showFullscreen ? '1' : '0',
      download: config.showDownload ? '1' : '0',
      menu: config.showMenu ? '1' : '0',
      search: config.showSearch ? '1' : '0',
      lang: config.defaultLanguage || 'es',
      mode: config.viewerMode || 'flip'
    });

    const width = config?.width || '100%';
    const height = config?.height || '800';
    const responsive = config?.responsive !== false;

    return `<!-- CatalogFlip Iframe Embed -->
<iframe
  src="${baseUrl}/embed/${catalogId}?${params?.toString()}"
  width="${width}"
  height="${height}px"
  frameborder="0"
  allowfullscreen
  style="border: none; ${responsive ? 'max-width: 100%;' : ''}"
  title="CatalogFlip Embedded Catalog"
></iframe>`;
  },

  // Generate JavaScript embed code (advanced)
  generateEmbedScript(catalogId, config = {}) {
    const baseUrl = window?.location?.origin;
    const configJson = JSON.stringify({
      catalogId,
      width: config?.width || '100%',
      height: config?.height || '800',
      responsive: config?.responsive !== false,
      initialPage: config?.initialPage || 1,
      theme: config?.theme || 'light',
      showNavigation: config?.showNavigation !== false,
      showSharing: config?.showSharing !== false,
      showZoom: config?.showZoom !== false,
      showFullscreen: config?.showFullscreen !== false,
      showDownload: config?.showDownload || false,
      showMenu: config?.showMenu || false,
      showSearch: config?.showSearch || false,
      defaultLanguage: config?.defaultLanguage || 'es',
      viewerMode: config?.viewerMode || 'flip',
      analyticsEnabled: config?.enableAnalytics !== false
    }, null, 2);

    return `<!-- CatalogFlip JavaScript Embed -->
<div id="catalogflip-embed-${catalogId}"></div>
<script>
  (function() {
    var config = ${configJson};
    
    var iframe = document.createElement('iframe');
    var params = new URLSearchParams({
      page: config.initialPage,
      theme: config.theme,
      nav: config.showNavigation ? '1' : '0',
      share: config.showSharing ? '1' : '0',
      zoom: config.showZoom ? '1' : '0',
      fullscreen: config.showFullscreen ? '1' : '0',
      download: config.showDownload ? '1' : '0',
      menu: config.showMenu ? '1' : '0',
      search: config.showSearch ? '1' : '0',
      lang: config.defaultLanguage || 'es',
      mode: config.viewerMode || 'flip'
    });

    iframe.src = '${baseUrl}/embed/' + config.catalogId + '?' + params.toString();
    iframe.width = config.width;
    iframe.height = config.height + 'px';
    iframe.frameBorder = '0';
    iframe.allowFullscreen = true;
    iframe.style.border = 'none';
    if (config.responsive) {
      iframe.style.maxWidth = '100%';
    }
    iframe.title = 'CatalogFlip Embedded Catalog';
    
    var container = document.getElementById('catalogflip-embed-' + config.catalogId);
    if (container) {
      container.appendChild(iframe);
    }
  })();
</script>`;
  },

  // Get catalog embed URL
  getCatalogEmbedUrl(catalogId) {
    const baseUrl = window?.location?.origin;
    return `${baseUrl}/embed/${catalogId}`;
  },

  // Copy embed code to clipboard
  async copyEmbedCode(code) {
    try {
      await navigator?.clipboard?.writeText(code);
      return true;
    } catch (error) {
      console.error('Failed to copy embed code:', error);
      return false;
    }
  }
};